import { yupResolver } from "@hookform/resolvers/yup";
import { LogoIcon } from "assets/icons";
import cn from "classnames";
import { t } from "i18next";
import { useManagerAuthTokens } from "modules/companyAdminAuth";
import { useForm, Controller } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { Button, Input } from "ui";

import { useLogin } from "../../apis";
import { UserRole } from "../../constants/userRole";
import { LoginSchema } from "../../helpers/validations";
import { useAuthTokens } from "../../hooks";
import { LoginFormData } from "./Login.types";

import styles from "./Login.module.scss";

const Login = () => {
  const navigate = useNavigate();

  const { setTokens } = useAuthTokens();
  const { setTokens: setManagerTokens } = useManagerAuthTokens();

  const { control, setError, handleSubmit } = useForm({
    mode: "onSubmit",
    defaultValues: {
      email: "",
      password: ""
    },
    resolver: yupResolver(LoginSchema)
  });

  const { mutate: mutateLogin, isLoading: isLoginLoading } = useLogin({
    meta: {
      preventDefaultBadRequest: true
    },
    onSuccess: (response) => {
      const { jwt, role } = response;

      setTokens(jwt.access, jwt.refresh, role);

      if (
        role === UserRole.DeliveryCompany ||
        role === UserRole.CompanyManager
      ) {
        // Bridge unified token into the existing managerInstance auth.
        setManagerTokens(jwt.access, jwt.refresh);
        navigate("/company");
      } else {
        navigate("/");
      }
    },
    onError: (error) => {
      const errorMessage = error.response?.data.message;

      setError("email", {
        message: Array.isArray(errorMessage) ? errorMessage[0] : errorMessage
      });
    }
  });

  const onSubmit = (formData: LoginFormData) => {
    mutateLogin(formData);
  };

  return (
    <div className={styles.login}>
      <LogoIcon />
      <form
        className={cn("box-shadow", styles.login__form)}
        onSubmit={handleSubmit(onSubmit)}
      >
        <Controller
          control={control}
          name="email"
          render={({ field, fieldState: { error } }) => (
            <Input
              label={t("Email")}
              {...field}
              errorMessage={error?.message}
              data-testid="login--email"
            />
          )}
        />
        <Controller
          name="password"
          control={control}
          render={({ field, fieldState: { error } }) => (
            <Input
              label="Password"
              {...field}
              isPassword
              errorMessage={error?.message}
              data-testid="login--password"
            />
          )}
        />
        <Button
          type="submit"
          loading={isLoginLoading}
          block
          data-testid="login--button"
        >
          {t("Login")}
        </Button>
      </form>
    </div>
  );
};

export default Login;
